import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Search, MapPin, Check, X } from 'lucide-react'
import api from '../../services/api'
import { todayInManaus } from '../../utils/dateManaus'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import ConfirmDialog from '../../components/ConfirmDialog'
import toast from 'react-hot-toast'

export default function DeliveryForm() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [selectedRecipients, setSelectedRecipients] = useState([])
  const [scheduledDate, setScheduledDate] = useState(
    todayInManaus()
  )
  const [batchErrors, setBatchErrors] = useState([])
  const [pendingDuplicates, setPendingDuplicates] = useState([])
  const [pendingResult, setPendingResult] = useState(null)
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false)

  // Busca destinatários
  const { data: recipientsData, isLoading: loadingRecipients } = useQuery({
    queryKey: ['recipients', 'deliveryPicker', search],
    queryFn: async () => {
      const response = await api.get('/api/recipients', {
        params: { search, limit: 20 }
      })
      return response.data.data.recipients
    },
  })

  // Mutation para criar entregas em lote - o branching (duplicidade,
  // confirmação, segunda chamada) fica todo em handleSubmit/handleConfirm
  // via mutateAsync, não aqui.
  const createDeliveries = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/deliveries/batch', data)
      return response.data.data
    },
    onSuccess: (data) => {
      if (data.created > 0) {
        queryClient.invalidateQueries({ queryKey: ['deliveries'] })
        queryClient.invalidateQueries({ queryKey: ['availableDeliveries'] })
      }
    },
  })

  const toggleRecipient = (recipient) => {
    setBatchErrors([])
    setSelectedRecipients((prev) =>
      prev.some((r) => r.id === recipient.id)
        ? prev.filter((r) => r.id !== recipient.id)
        : [...prev, recipient]
    )
  }

  const removeRecipient = (recipientId) => {
    setBatchErrors([])
    setSelectedRecipients((prev) => prev.filter((r) => r.id !== recipientId))
  }

  // Mesma lógica de sempre (BUG-77): sem erro -> sucesso total, navega.
  // Com erro -> toast parcial (se algo foi criado), mantém só os que
  // erraram selecionados pra reenvio manual.
  const finalizeResult = ({ created, errors }) => {
    if (errors.length === 0) {
      toast.success(`${created} entrega(s) criada(s) com sucesso!`)
      navigate('/deliveries')
      return
    }

    if (created > 0) {
      toast.success(`${created} entrega(s) criada(s) com sucesso!`)
    }

    setBatchErrors(errors)
    setSelectedRecipients((prev) =>
      prev.filter((r) => errors.some((e) => e.recipientId === r.id))
    )
  }

  const handleSubmit = async () => {
    if (selectedRecipients.length === 0) {
      toast.error('Selecione ao menos um destinatário')
      return
    }

    try {
      const { created, errors, duplicates } = await createDeliveries.mutateAsync({
        scheduledDate,
        deliveries: selectedRecipients.map((r) => ({ recipientId: r.id })),
      })

      if (duplicates.length === 0) {
        finalizeResult({ created, errors })
        return
      }

      setBatchErrors([])
      setPendingResult({ created, errors })
      setPendingDuplicates(
        duplicates.map((d) => ({
          recipientId: d.recipientId,
          severity: d.severity,
          name: selectedRecipients.find((r) => r.id === d.recipientId)?.name || d.recipientId,
        }))
      )
      setDuplicateDialogOpen(true)
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao criar entregas')
    }
  }

  const handleConfirmDuplicates = async () => {
    setDuplicateDialogOpen(false)
    const prior = pendingResult

    try {
      const { created, errors } = await createDeliveries.mutateAsync({
        scheduledDate,
        deliveries: pendingDuplicates.map((d) => ({
          recipientId: d.recipientId,
          confirmDuplicate: true,
        })),
      })

      finalizeResult({
        created: prior.created + created,
        errors: [...prior.errors, ...errors],
      })
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao criar entregas')
      finalizeResult(prior)
    } finally {
      setPendingDuplicates([])
      setPendingResult(null)
    }
  }

  const handleCancelDuplicates = () => {
    const duplicateIds = new Set(pendingDuplicates.map((d) => d.recipientId))
    setSelectedRecipients((prev) => prev.filter((r) => !duplicateIds.has(r.id)))

    setDuplicateDialogOpen(false)
    const prior = pendingResult
    setPendingDuplicates([])
    setPendingResult(null)

    finalizeResult(prior)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/deliveries')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="page-title">Nova Entrega</h1>
          <p className="page-subtitle">Selecione um ou mais destinatários e agende a entrega.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Seleção de destinatário */}
        <Card>
          <h3 className="font-semibold text-gray-900 mb-4">
            1. Selecione os Destinatários
            {selectedRecipients.length > 0 && (
              <span className="text-primary-600"> ({selectedRecipients.length} selecionado{selectedRecipients.length > 1 ? 's' : ''})</span>
            )}
          </h3>

          {/* Busca */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar destinatário..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field pl-10"
            />
          </div>

          {/* Lista de destinatários */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {loadingRecipients ? (
              <div className="text-center py-8 text-gray-500">Carregando...</div>
            ) : recipientsData?.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Nenhum destinatário encontrado.
                <br />
                <button
                  onClick={() => navigate('/recipients/new')}
                  className="text-primary-600 hover:underline mt-2"
                >
                  Cadastrar novo
                </button>
              </div>
            ) : (
              recipientsData?.map((recipient) => {
                const isSelected = selectedRecipients.some((r) => r.id === recipient.id)
                return (
                  <button
                    key={recipient.id}
                    onClick={() => toggleRecipient(recipient)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                      isSelected
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{recipient.name}</p>
                        <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />
                          {recipient.address?.street || recipient.addressStreet}, {recipient.address?.number || recipient.addressNumber} - {recipient.address?.neighborhood || recipient.addressNeighborhood}, {recipient.address?.city || recipient.addressCity}
                        </p>
                      </div>
                      {isSelected && (
                        <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                  </button>
                )
              })
            )}
          </div>
        </Card>

        {/* Detalhes da entrega */}
        <Card>
          <h3 className="font-semibold text-gray-900 mb-4">2. Detalhes da Entrega</h3>

          {/* Destinatários selecionados */}
          {selectedRecipients.length > 0 && (
            <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
              {selectedRecipients.map((recipient) => (
                <div
                  key={recipient.id}
                  className="flex items-center justify-between gap-2 bg-primary-50 p-3 rounded-lg"
                >
                  <div className="min-w-0">
                    <p className="text-gray-900 font-medium truncate">{recipient.name}</p>
                    <p className="text-sm text-gray-600 truncate">
                      {recipient.address?.street || recipient.addressStreet}, {recipient.address?.number || recipient.addressNumber} - {recipient.address?.neighborhood || recipient.addressNeighborhood}
                    </p>
                  </div>
                  <button
                    onClick={() => removeRecipient(recipient.id)}
                    className="shrink-0 p-1 text-gray-400 hover:text-red-600 transition-colors"
                    aria-label={`Remover ${recipient.name}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Erros do lote */}
          {batchErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg mb-4">
              <p className="text-sm text-red-700 font-medium mb-2">
                {batchErrors.length} destinatário(s) não puderam ser processados:
              </p>
              <ul className="text-sm text-red-600 list-disc list-inside space-y-1">
                {batchErrors.map((err) => (
                  <li key={err.recipientId}>
                    {selectedRecipients.find((r) => r.id === err.recipientId)?.name || err.recipientId}: {err.error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Data agendada */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data da Entrega
            </label>
            <input
              type="date"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              className="input-field"
              min={todayInManaus()}
            />
          </div>

          {/* Botões */}
          <div className="flex gap-4">
            <Button
              variant="secondary"
              fullWidth
              onClick={() => navigate('/deliveries')}
            >
              Cancelar
            </Button>
            <Button
              fullWidth
              onClick={handleSubmit}
              isLoading={createDeliveries.isPending}
              disabled={selectedRecipients.length === 0}
            >
              Criar Entrega{selectedRecipients.length > 1 ? 's' : ''}
            </Button>
          </div>
        </Card>
      </div>

      <ConfirmDialog
        open={duplicateDialogOpen}
        title="Destinatários já têm entrega hoje"
        message={`${pendingDuplicates.length} destinatário(s) já têm entrega marcada para essa data.`}
        confirmLabel="Criar mesmo assim"
        cancelLabel="Não criar esses"
        onConfirm={handleConfirmDuplicates}
        onCancel={handleCancelDuplicates}
      >
        <ul className="mt-4 space-y-2 max-h-48 overflow-y-auto">
          {pendingDuplicates.map((d) => (
            <li key={d.recipientId} className="flex items-center justify-between gap-2 text-sm">
              <span className="text-gray-900 truncate">{d.name}</span>
              <span
                className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${
                  d.severity === 'forte'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-amber-100 text-amber-700'
                }`}
              >
                {d.severity === 'forte' ? 'provável duplicata' : 'pedido diferente, mesmo destinatário'}
              </span>
            </li>
          ))}
        </ul>
      </ConfirmDialog>
    </div>
  )
}
